"use client"

import type React from "react"

import { useState } from "react"
import { ChevronDown, ChevronUp } from "lucide-react"

interface AccordionItemProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}

export function AccordionItem({ title, children, defaultOpen = false }: AccordionItemProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border border-gray-700 rounded-md mb-4 overflow-hidden">
      <button
        className="w-full p-4 text-left font-medium flex justify-between items-center bg-gray-800 hover:bg-gray-700 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{title}</span>
        {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
      </button>
      {isOpen && <div className="p-4 bg-gray-900">{children}</div>}
    </div>
  )
}

export function HardwareAccordion() {
  return (
    <div className="my-6">
      <div className="mb-4 text-red-500 font-medium">
        Recommended brands to avoid: XFX, PowerColor, HIS and VisionTek. As they run a vBIOS that doesn't work well with
        macOS.
      </div>

      <div className="mb-4 text-red-500 font-medium">
        RTX and GTX 10xx/1xxx NOT supported. No support in macOS Sonoma for Nvidia.
      </div>

      <AccordionItem title="AMD RX 5xxx/6xxx Cards" defaultOpen={true}>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Radeon RX 6950 XT* <span className="text-yellow-500">(*Fake ID spoof required!)</span>
          </li>
          <li>Radeon RX 6900 XT*</li>
          <li>Radeon RX 6800 XT</li>
          <li>Radeon RX 6800</li>
          <li>Radeon RX 6650 XT</li>
          <li>Radeon RX 6600 XT</li>
          <li>Radeon RX 6600</li>
          <li>Radeon RX 5700 XT</li>
          <li>Radeon RX 5700</li>
          <li>Radeon RX 5600 XT</li>
          <li>Radeon RX 5600</li>
          <li>Radeon RX 5500 XT</li>
          <li>Radeon RX 5500</li>
        </ul>
      </AccordionItem>

      <AccordionItem title="Native AMD RX Cards">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Radeon Vega 64*{" "}
            <span className="text-yellow-500">
              (Temperamental, *Can sometimes be tricky to get fully working with PCI-Passthrough, might get issues with
              audio.)
            </span>
          </li>
          <li>Radeon Vega 56*</li>
          <li>Radeon VII</li>
          <li>Radeon RX 590</li>
          <li>Radeon RX 580*</li>
          <li>Radeon RX 570</li>
          <li>Radeon RX 560X</li>
          <li>Radeon RX 560</li>
          <li>Radeon RX 550*</li>
          <li>Radeon RX 480</li>
          <li>Radeon RX 470D</li>
          <li>Radeon RX 470</li>
          <li>Radeon RX 460</li>
          <li>Radeon Pro WX 9100</li>
          <li>Radeon Pro WX 7100</li>
          <li>Radeon Pro WX 5100</li>
          <li>Radeon Pro WX 4100</li>
        </ul>
      </AccordionItem>

      <AccordionItem title="Legacy Nvidia Cards">
        <ul className="list-disc pl-5 space-y-1">
          <li>
            Geforce GTX 780 Ti <span className="text-green-500">(Compatible)</span>
          </li>
          <li>Geforce GTX 780</li>
          <li>Geforce GTX 770</li>
          <li>Geforce GTX 760 Ti</li>
          <li>Geforce GTX 760</li>
          <li>Geforce GT 740*</li>
          <li>Geforce GT 730*</li>
          <li>Geforce GT 720</li>
          <li>Geforce GT 710*</li>
          <li>Geforce GTX 690</li>
          <li>Geforce GTX 680</li>
          <li>Geforce GTX 670</li>
          <li>Geforce GTX 660 Ti</li>
          <li>Geforce GTX 660*</li>
          <li>Geforce GTX 650*</li>
          <li>Geforce GT 640*</li>
          <li>Geforce GT 635</li>
          <li>Geforce GT 630*</li>
          <li>Quadro Cards*</li>
        </ul>
      </AccordionItem>
    </div>
  )
}

export function WifiBluetoothAccordion() {
  return (
    <div className="my-6">
      <p className="mb-4">
        Most devices should work just fine with Monterey and Ventura. We can't confirm whether every single one will
        work 100%, but we do know for sure that there has been a change of drivers shipped with macOS Sonoma. Drivers
        for certain Broadcom devices has been deprecated, so do keep that in mind!
      </p>

      <div className="mb-4 p-4 bg-yellow-900/30 border border-yellow-700 rounded-md">
        <h4 className="font-bold text-yellow-500 mb-2">Notice</h4>
        <p>
          You yourself are responsible for which one of the adapters listed below. If the one you bought doesn't work as
          intended, there's nothing we can do or take any responsibility for!
        </p>
        <p className="mt-2">
          Should you already have a built-in Wi-Fi/Bluetooth card installed on your motherboard and it's incompatible,
          make sure to remove it or disable in BIOS if possible. Will cause interference with any external cards
          installed.
        </p>
      </div>

      <AccordionItem title="Fenvi Cards" defaultOpen={true}>
        <div className="mb-4 p-3 bg-blue-900/30 border border-blue-700 rounded-md">
          <p>
            As of right now, using Fenvi cards through Proxmox for macOS, you'll have to stick to Monterey/Ventura and
            the 5.15.53 kernel is recommended. Especially if you're using AMD GPU for passthrough. The 6.2.* kernel will
            cause the VM to freeze and restart upon connecting to access points.
          </p>
        </div>

        <ul className="list-disc pl-5 space-y-1">
          <li>
            Fenvi FV-HB1200 | Desktop PCI-E <span className="text-green-500">(Recommended)</span>
          </li>
          <li>
            Fenvi T191 | Desktop PCI-E <span className="text-green-500">(Recommended)</span>
          </li>
          <li>Fenvi BCM94360NG | M.2 Card</li>
          <li>Fenvi BCM94360CD | Desktop PCI-E</li>
          <li>WDXUN BCM943602CS | Desktop PCI-E</li>
          <li>Fenvi BCM94360NG | Antenna Kit</li>
          <li>WIRCARD BCM94360CS2 | M.2 Card</li>
          <li>BCM94360CS2 | M.2 Adapter</li>
          <li>BCM94360CS2/BCM943224PCIEBT2 | M.2 Adapter</li>
          <li>BCM943602CS BCM94360CS2 BCM943224PCIEBT2 BCM94331CD | M.2 to USB Header adapter</li>
          <li>Antenna Kits</li>
          <li>Built-in antenna</li>
        </ul>
      </AccordionItem>

      <AccordionItem title="Compatible USB WiFi Adapters">
        <div className="mb-4">
          <p className="mb-2">
            For Intel devices, <span className="text-blue-400 cursor-pointer">click here</span>.
          </p>
          <p>
            <span className="text-blue-400 cursor-pointer">Click here</span> for more information.
            <span className="text-gray-400 text-sm">(list borrowed from chris1111)</span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          <div>Alfa AWUS036AC</div>
          <div>Alfa AWUS036ACH</div>
          <div>Archer T2U Plus (AC600)</div>
          <div>Archer T2U NANO, MINI, AC600</div>
          <div>Archer T3U</div>
          <div>Archer T3U Plus</div>
          <div>Archer T2U MINI V3</div>
          <div>Archer T2U Plus</div>
          <div>ArcherT4U V1, V2, V3</div>
          <div>Archer T9UH V1, V2</div>
          <div>ASUS USB AC68</div>
          <div>ASUS USB-N13</div>
          <div>ASUS USB Nano-AC53</div>
          <div>BrosTrend FBA_AC3</div>
          <div>COMFAST CF-811AC</div>
          <div>COMFAST CF-812AC</div>
          <div>Comfast CF-758F</div>
          <div>Comfast CF-WU810N</div>
          <div>Cudy WU1300S</div>
          <div>Cudy WU700</div>
          <div>Cudy WU650</div>
          <div>CXFTEOXK</div>
          <div>DLink DWA-121 N150</div>
          <div>DLink DWA-131 E</div>
          <div>DLink DWA-171 C</div>
          <div>DLink DWA-182 D</div>
          <div>DLink DWA-192 A</div>
          <div>EDIMAX EW-7611UCB</div>
          <div>EDIMAX EW-7722UTn V2</div>
          <div>EDIMAX_EW-7822ULC</div>
          <div>EDIMAX EW-7612Uan V2</div>
          <div>EDIMAX EW-7833UAC</div>
          <div>EDIMAX N300</div>
          <div>EDIMAX EW-7811Un (N150)</div>
          <div>EDUP EP-AC1689</div>
          <div>Fenvi AC1300 (RTL8812bu)</div>
          <div>FILOWA USB WiFi-RTL8812BU</div>
          <div>Foktech AC600 Nano</div>
          <div>Jensen Eagle 100-AC</div>
          <div>Kextech MINI USB RTL8192</div>
          <div>Linksys WUSB6300 V2</div>
          <div>Linksys WUSB6400M</div>
          <div>M-Tech UW-01 USB</div>
          <div>Netgear A6100</div>
          <div>Netgear A6150</div>
          <div>Netgear A7000</div>
          <div>Netis WF2120 N Nano USB</div>
          <div>Plexgear AC1200</div>
          <div>Sitecom WLA7100</div>
          <div>TL-WN823Nv2/v3</div>
          <div>TL-WN725Nv3</div>
          <div>TL-WN723Nv2/v3</div>
          <div>TL-WN722Nv2/v3</div>
          <div>TL-WN821Nv6</div>
          <div>TL-WN822Nv4/v5</div>
          <div>TENDA W311-MINI</div>
          <div>Tenda U3 Mini</div>
          <div>TENDA U12</div>
          <div>TRENDnet N150 Micro</div>
          <div>TRENDnet TEW-808UBM</div>
          <div>TRENDnet TEW-908UB</div>
          <div>YUNCLOUD Realtek (RTL8814AU)</div>
          <div>ZAPO W58L (RTL881lAU)</div>
        </div>
      </AccordionItem>
    </div>
  )
}
